import { Color, Spacing } from '@persona-id/design-tokens';
import styled from 'styled-components';

import Accordion from 'dashboard/components/Accordion';
import { MonoSpan } from 'dashboard/components/Typography';
import AsyncSelfie from 'dashboard/components-lib/AsyncSelfie';
import TableLayout, { TableLinkTR, TableTD, TableTH } from 'dashboard/layouts/TableLayout';
import {
  THRESHOLD_EXACT_MATCH_FACE,
  THRESHOLD_VERY_HIGH_FACE,
} from 'dashboard/lib/constants/similarity-selfie';
import { Account, SimilarAccount } from 'dashboard/models/account';
import { ProductFeatureName } from 'dashboard/models/organization';
import { Selfie } from 'dashboard/models/selfie';
import Card from 'design-system/components/Card';
import Sticker, { LabelBackgrounds } from 'design-system/components/Sticker';
import { Paragraph } from 'design-system/components/Text';
import ImageAvatar from 'images/dashboard/avatar.svg';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';
import useProductFeature from 'lib/hooks/useProductFeature';
import { ApiModel } from 'lib/interfaces/api';

const Container = styled('div')`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  background: ${Color.PrimaryWhite};

  & > div {
    margin-right: ${Spacing[2]};
    margin-bottom: ${Spacing[2]};
  }
`;

const TableContainer = styled.div`
  padding-top: ${Spacing[2]};
`;

const AVATAR_WIDTH = 128;
const AVATAR_HEIGHT = 128;

interface AccountSelfiesSectionProps {
  account: Account;
  selfies: Selfie[];
  updateMainSelfie?: (selfieId: string) => Promise<void>;
}

const SimilarAccountsTable = ({ accounts }: { accounts: SimilarAccount[] }) => {
  return (
    <TableContainer>
      <TableLayout
        headerTR={
          <>
            <TableTH>Name</TableTH>
            <TableTH>Account ID</TableTH>
            <TableTH>Reference ID</TableTH>
            <TableTH>Confidence</TableTH>
          </>
        }
        nullState={<div />}
      >
        {accounts.map((account) => {
          // Mirrors logic in GraphHoverCardAccountSimilaritySelfie
          const {
            text: confidenceText,
            color: confidenceColor,
          }: { text: string; color: LabelBackgrounds } =
            account.confidence >= THRESHOLD_EXACT_MATCH_FACE
              ? {
                  text: 'Exact Match',
                  color: 'red',
                }
              : account.confidence > THRESHOLD_VERY_HIGH_FACE
                ? {
                    text: 'Very High',
                    color: 'pink',
                  }
                : {
                    text: 'High',
                    color: 'yellow',
                  };
          return (
            <TableLinkTR to={`/dashboard/accounts/${account.id}`} key={account.id}>
              <TableTD>
                {account.name ? (
                  <MonoSpan>{account.name}</MonoSpan>
                ) : (
                  <Paragraph color='disabled'>No name</Paragraph>
                )}
              </TableTD>
              <TableTD>
                <MonoSpan>{account.id}</MonoSpan>
              </TableTD>
              <TableTD>
                {account['reference-id'] ? (
                  <MonoSpan>{account['reference-id']}</MonoSpan>
                ) : (
                  <Paragraph color='disabled'>No reference ID</Paragraph>
                )}
              </TableTD>
              <TableTD>
                <Sticker variant={confidenceColor}>{confidenceText}</Sticker>
              </TableTD>
            </TableLinkTR>
          );
        })}
      </TableLayout>
    </TableContainer>
  );
};

const AccountSelfiesSection = ({
  account,
  selfies,
  updateMainSelfie,
}: AccountSelfiesSectionProps) => {
  const isAccountsSelfieSimilarityEnabled = useProductFeature(
    ProductFeatureName.AccountsSelfieSimilarity,
  );
  const isShowIndexedEnabled = useFeatureFlag('graph:show-selfie-registry-registration-status');
  return (
    <>
      <Card>
        <Card.Body>
          <Card.Title>Selfies</Card.Title>
          <h4>Main</h4>
          <Container data-test='account-selfies-container'>
            {account.attributes['selfie-photo-url'] != null ? (
              <AsyncSelfie
                height={`${AVATAR_HEIGHT}px`}
                width={`${AVATAR_WIDTH}px`}
                value={[{ url: account.attributes['selfie-photo-url'] }]}
                placeholder={<ImageAvatar height={AVATAR_HEIGHT} width={AVATAR_WIDTH} />}
                key={account.attributes['selfie-photo-url']}
                modalLabel='Selfie Photo'
                useDropdownUI={isShowIndexedEnabled}
                showIndexedStatus={false}
                enableSetAsMain={false}
                sourceToken={account.id}
              />
            ) : (
              <ImageAvatar height={AVATAR_HEIGHT} width={AVATAR_WIDTH} />
            )}
          </Container>
          <h4>Other Collected</h4>
          <Container data-test='account-selfies-container'>
            {selfies
              .filter((selfie: Selfie) => !selfie.attributes['redacted-at'])
              .map((selfie: Selfie) => {
                if (selfie.type === ApiModel.SelfieCenterOnly) {
                  return (
                    <AsyncSelfie
                      height={`${AVATAR_HEIGHT}px`}
                      width={`${AVATAR_WIDTH}px`}
                      value={[{ url: selfie.attributes['photo-url'] }]}
                      placeholder={<ImageAvatar height={AVATAR_HEIGHT} width={AVATAR_WIDTH} />}
                      key={selfie.id}
                      modalLabel='Selfie Photo'
                      registryRegistrationData={selfie.attributes['registry-registration-data']}
                      useDropdownUI={isShowIndexedEnabled}
                      showIndexedStatus={isShowIndexedEnabled}
                      updateMainSelfie={() => updateMainSelfie && updateMainSelfie(selfie.id)}
                      sourceToken={selfie.id}
                      enableSetAsMain
                    />
                  );
                } else if (selfie.type === ApiModel.SelfieProfileAndCenter) {
                  // Return just center photo for now until the design supports
                  // something more advanced for ProfileAndCenter
                  return (
                    <AsyncSelfie
                      height={`${AVATAR_HEIGHT}px`}
                      width={`${AVATAR_WIDTH}px`}
                      value={[{ url: selfie.attributes['center-photo-url'] }]}
                      placeholder={<ImageAvatar height={AVATAR_HEIGHT} width={AVATAR_WIDTH} />}
                      key={selfie.id}
                      modalLabel='Selfie Center Photo'
                      registryRegistrationData={
                        // TODO (unzi) - remove safe navigation once serializer code is merged
                        selfie.attributes['center-photo']?.['registry-registration-data']
                      }
                      useDropdownUI={isShowIndexedEnabled}
                      showIndexedStatus={isShowIndexedEnabled}
                      updateMainSelfie={() => updateMainSelfie && updateMainSelfie(selfie.id)}
                      sourceToken={selfie.id}
                      enableSetAsMain
                    />
                  );
                }
              })}
          </Container>
          {isAccountsSelfieSimilarityEnabled &&
          account.attributes['similar-accounts-by-selfie']?.length ? (
            <Accordion title='Seen in other accounts' defaultExpanded={true}>
              <SimilarAccountsTable accounts={account.attributes['similar-accounts-by-selfie']} />
            </Accordion>
          ) : undefined}
        </Card.Body>
      </Card>
    </>
  );
};

export default AccountSelfiesSection;
